import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Globe, Clock, Link as LinkIcon, Copy, Phone, ShieldOff, Trash2, Plus, Wifi } from "lucide-react";
import { toast } from "sonner";

export default function Configuracion() {
  const [suc, setSuc] = useState(null);
  const [form, setForm] = useState({});
  const [bloqueados, setBloqueados] = useState([]);
  const [nuevoTel, setNuevoTel] = useState("");
  const [nuevoTelMotivo, setNuevoTelMotivo] = useState("");
  const [ipsBloqueadas, setIpsBloqueadas] = useState([]);
  const [nuevoIp, setNuevoIp] = useState("");
  const [nuevoIpMotivo, setNuevoIpMotivo] = useState("");

  const load = () => api.get("/sucursal/me").then((r) => { setSuc(r.data); setForm({
    pedidos_web_activos: r.data.pedidos_web_activos,
    pedidos_web_hora_inicio: r.data.pedidos_web_hora_inicio,
    pedidos_web_hora_fin: r.data.pedidos_web_hora_fin,
  }); });

  const loadBloqueados = () => api.get("/telefonos-bloqueados").then((r) => setBloqueados(r.data)).catch(() => {});
  const loadIps = () => api.get("/ips-bloqueadas").then((r) => setIpsBloqueadas(r.data)).catch(() => {});
  useEffect(() => { load(); loadBloqueados(); loadIps(); }, []);

  const save = async () => {
    try {
      await api.patch("/sucursal/me/pedidos-web", form);
      toast.success("Configuración guardada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bloquearTel = async () => {
    if (!nuevoTel.trim()) return toast.error("Captura un teléfono");
    try {
      await api.post("/telefonos-bloqueados", { telefono: nuevoTel.trim(), motivo: nuevoTelMotivo.trim() || null });
      toast.success("Teléfono bloqueado");
      setNuevoTel(""); setNuevoTelMotivo("");
      loadBloqueados();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const desbloquearTel = async (id) => {
    try {
      await api.delete(`/telefonos-bloqueados/${id}`);
      toast.success("Teléfono desbloqueado");
      loadBloqueados();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bloquearIp = async () => {
    if (!nuevoIp.trim()) return toast.error("Captura una IP");
    try {
      await api.post("/ips-bloqueadas", { ip: nuevoIp.trim(), motivo: nuevoIpMotivo.trim() || null });
      toast.success("IP bloqueada");
      setNuevoIp(""); setNuevoIpMotivo("");
      loadIps();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const desbloquearIp = async (id) => {
    try {
      await api.delete(`/ips-bloqueadas/${id}`);
      toast.success("IP desbloqueada");
      loadIps();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!suc) return <div className="text-muted-foreground">Cargando…</div>;
  const publicUrl = `${window.location.origin}/pedido/${suc.id}`;
  const trackUrl = `${window.location.origin}/seguimiento`;
  const copyUrl = (url) => { navigator.clipboard.writeText(url); toast.success("URL copiada"); };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-black tracking-tight">Configuración</h2>
        <p className="text-sm text-muted-foreground mt-1">Pedidos web, anti-spam y datos de tu sucursal.</p>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold flex items-center gap-2"><Globe className="h-5 w-5 text-primary" />Pedidos web públicos</h3>
            <Switch checked={!!form.pedidos_web_activos} onCheckedChange={(v) => setForm({ ...form, pedidos_web_activos: v })} data-testid="web-active-switch" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Hora inicio</Label>
              <Input type="time" value={form.pedidos_web_hora_inicio} onChange={(e) => setForm({ ...form, pedidos_web_hora_inicio: e.target.value })} />
            </div>
            <div>
              <Label>Hora fin</Label>
              <Input type="time" value={form.pedidos_web_hora_fin} onChange={(e) => setForm({ ...form, pedidos_web_hora_fin: e.target.value })} />
            </div>
          </div>
          <Button onClick={save} data-testid="save-config-btn">Guardar cambios</Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-3">
          <h3 className="text-lg font-bold flex items-center gap-2"><LinkIcon className="h-5 w-5 text-primary" />Enlaces públicos para clientes</h3>
          <div>
            <Label className="text-xs">Pedido en línea</Label>
            <div className="flex gap-2">
              <Input readOnly value={publicUrl} className="font-mono text-sm" />
              <Button variant="outline" onClick={() => copyUrl(publicUrl)} data-testid="copy-pedido-url"><Copy className="h-4 w-4" /></Button>
            </div>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="text-primary text-sm hover:underline">Abrir vista pública →</a>
          </div>
          <div>
            <Label className="text-xs">Seguimiento de pedido</Label>
            <div className="flex gap-2">
              <Input readOnly value={trackUrl} className="font-mono text-sm" />
              <Button variant="outline" onClick={() => copyUrl(trackUrl)} data-testid="copy-track-url"><Copy className="h-4 w-4" /></Button>
            </div>
            <a href={trackUrl} target="_blank" rel="noreferrer" className="text-primary text-sm hover:underline">Abrir seguimiento →</a>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-bold flex items-center gap-2"><ShieldOff className="h-5 w-5 text-primary" />Anti-spam: Teléfonos bloqueados</h3>
          <p className="text-xs text-muted-foreground">Los pedidos públicos desde estos números serán rechazados automáticamente.</p>
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
            <Input value={nuevoTel} onChange={(e) => setNuevoTel(e.target.value)} placeholder="55 1234 5678" data-testid="bloq-tel-input" />
            <Input value={nuevoTelMotivo} onChange={(e) => setNuevoTelMotivo(e.target.value)} placeholder="Motivo (opcional)" data-testid="bloq-tel-motivo" />
            <Button onClick={bloquearTel} data-testid="bloq-tel-add"><Plus className="h-4 w-4 mr-1" />Bloquear</Button>
          </div>
          <div className="space-y-1">
            {bloqueados.length === 0 && <div className="text-sm text-muted-foreground">Sin teléfonos bloqueados.</div>}
            {bloqueados.map((b) => (
              <div key={b.id} className="flex items-center justify-between border-b border-border py-2 text-sm" data-testid={`bloq-tel-row-${b.telefono}`}>
                <div className="flex items-center gap-2">
                  <Phone className="h-3 w-3 text-muted-foreground" />
                  <span className="font-mono font-bold">{b.telefono}</span>
                  {b.motivo && <span className="text-xs text-muted-foreground">— {b.motivo}</span>}
                </div>
                <Button size="icon" variant="ghost" onClick={() => desbloquearTel(b.id)} title="Desbloquear" data-testid={`bloq-tel-del-${b.telefono}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-bold flex items-center gap-2"><Wifi className="h-5 w-5 text-primary" />Anti-spam: IPs bloqueadas</h3>
          <p className="text-xs text-muted-foreground">Bloquea conexiones específicas. El sistema ya limita a 10 pedidos/hora por IP automáticamente.</p>
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
            <Input value={nuevoIp} onChange={(e) => setNuevoIp(e.target.value)} placeholder="192.168.1.1" data-testid="bloq-ip-input" />
            <Input value={nuevoIpMotivo} onChange={(e) => setNuevoIpMotivo(e.target.value)} placeholder="Motivo (opcional)" data-testid="bloq-ip-motivo" />
            <Button onClick={bloquearIp} data-testid="bloq-ip-add"><Plus className="h-4 w-4 mr-1" />Bloquear</Button>
          </div>
          <div className="space-y-1">
            {ipsBloqueadas.length === 0 && <div className="text-sm text-muted-foreground">Sin IPs bloqueadas.</div>}
            {ipsBloqueadas.map((b) => (
              <div key={b.id} className="flex items-center justify-between border-b border-border py-2 text-sm" data-testid={`bloq-ip-row-${b.ip}`}>
                <div className="flex items-center gap-2">
                  <Wifi className="h-3 w-3 text-muted-foreground" />
                  <span className="font-mono font-bold">{b.ip}</span>
                  {b.motivo && <span className="text-xs text-muted-foreground">— {b.motivo}</span>}
                </div>
                <Button size="icon" variant="ghost" onClick={() => desbloquearIp(b.id)} title="Desbloquear" data-testid={`bloq-ip-del-${b.ip}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-bold mb-3">Detalles de la sucursal</h3>
          <div className="text-sm space-y-1">
            <div><span className="text-muted-foreground">Nombre:</span> <span className="font-semibold">{suc.nombre}</span></div>
            <div><span className="text-muted-foreground">Dirección:</span> {suc.direccion || "-"}</div>
            <div><span className="text-muted-foreground">Teléfono:</span> {suc.telefono || "-"}</div>
            <div><span className="text-muted-foreground">Estatus de licencia:</span> <span className="font-bold">{suc.estatus_licencia}</span></div>
            <div className="flex items-center gap-2"><Clock className="h-3 w-3 text-muted-foreground" /><span className="text-muted-foreground">Vencimiento:</span> {fmtDate(suc.fecha_vencimiento)}</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
